import { addSecondaryCalendarInfo } from "../../api/appointments/calendar-management";
import { updateAppointmentTypeErrors } from "../../redux/actions/CalendarManagementActions";
import store from "../../redux/store";
import { TEMP_CALENDAR_ID } from "../../services/calendar-management/components/calendar-section/CalendarSection";
import { isOnlyLetters } from "../../validation/input-validations";
import { compareObjects, getOrganizationFeatureFlag, validateField } from "../base-logic";
import { OPENING_HOURS_KEY } from "../knowledge-base/knowledge-base-logic";

export const CALENDAR_MANAGEMENT_KEY = "calendar-management";

export const invalidCalendarManagementKeys = [
  "invalid-calendar-connection",
  "no-scheduling-windows",
  "no-appointment-types",
  "min-appointment-types",
  "invalid-scheduling-window",
  "invalid-appointment-type",
];

export const getAppointmentTypeErrors = (appointmentType) => {
  const config = store.getState().calendarManagement.config;
  const voices = store.getState().info.voices;
  const { agentLanguage, updatedVoice } = store.getState().info;
  const selectedVoice = voices?.find((voice) => voice.id === updatedVoice);
  const selectedVoiceLanguage = selectedVoice?.language || agentLanguage || "en-US";
  const errors = {};

  errors.type = validateField(
    true,
    "Appointment name",
    appointmentType.type,
    config.minAppointmentTypeCharacters,
    selectedVoiceLanguage,
    isOnlyLetters
  );
  errors.duration = validateField(true, "Duration", appointmentType.duration, 0);
  errors.description = validateField(
    true,
    "Appointment Description",
    appointmentType.description,
    config.minAppointmentDescriptionCharacters,
    selectedVoiceLanguage
  );

  return errors;
};

export const getAppointmentTypeFieldError = (knowledgeItem, key, value) => {
  const config = store.getState().calendarManagement.config;
  const voices = store.getState().info.voices;
  const { agentLanguage, updatedVoice } = store.getState().info;
  const selectedVoice = voices?.find((voice) => voice.id === updatedVoice);
  const selectedVoiceLanguage = selectedVoice?.language || agentLanguage || "en-US";

  switch (key) {
    case "type":
      return validateField(
        true,
        "Appointment name",
        value.trim(),
        config.minAppointmentTypeCharacters,
        selectedVoiceLanguage,
        isOnlyLetters
      );
    case "description":
      return validateField(
        true,
        "Appointment Description",
        value.trim(),
        config.minAppointmentDescriptionCharacters,
        selectedVoiceLanguage
      );
    case "duration":
      return validateField(true, "Duration", value, 0);
    case "postSchedulingTextMessage":
      return validateField(
        config.minAppointmentPostSchedulingTextMessageCharacters > 0,
        "Confirmation text message",
        value?.trim(),
        config.minAppointmentPostSchedulingTextMessageCharacters
      );
    default:
      return "";
  }
};

export const hasChangesInCalendarManagement = () => {
  const calendarManagement = store.getState().calendarManagement;

  if (!compareObjects(calendarManagement.calendarInfo, calendarManagement.updatedCalendarInfo)) {
    return true;
  }

  if (calendarManagement.isSameAsOpeningHours !== calendarManagement.updatedIsSameAsOpeningHours) {
    return true;
  }

  if (!compareObjects(calendarManagement.schedulingWindows, calendarManagement.updatedSchedulingWindows)) {
    return true;
  }

  if (!compareObjects(calendarManagement.appointmentTypes, calendarManagement.updatedAppointmentTypes)) {
    return true;
  }

  return false;
};

export const validateCalendar = () => {
  const calendarManagement = store.getState().calendarManagement;
  const calendarFound = calendarManagement.calendarFound;
  const oldCalendarIsValid = calendarManagement.calendarInfo.id !== null && calendarManagement.calendarFound;
  const isCalendarChanged = calendarManagement.calendarInfo.id !== calendarManagement.updatedCalendarInfo.id;
  const isExistingCalendarIsValid =
    calendarFound && calendarManagement.calendarInfo.id === calendarManagement.updatedCalendarInfo.id;

  if (!calendarManagement.calendarInfo.id && calendarManagement.updatedCalendarInfo.id === TEMP_CALENDAR_ID) {
    return true;
  }

  if (isCalendarChanged) {
    return true;
  }

  if (!isExistingCalendarIsValid) {
    return false;
  }

  return oldCalendarIsValid;
};

export const validateCalendarManagement = () => {
  const calendarManagement = store.getState().calendarManagement;
  const knowledgeBase = store.getState().knowledgeBase;

  const openingHours =
    knowledgeBase.updatedKnowledgeBaseItems.find((item) => item.type === OPENING_HOURS_KEY)?.value || [];

  if (!calendarManagement || !calendarManagement.updatedCalendarInfo.id) {
    return { valid: true };
  }

  const isCalendarValid = validateCalendar();
  if (
    !isCalendarValid &&
    (calendarManagement.updatedAppointmentTypes.length > 0 || calendarManagement.updatedSchedulingWindows.length > 0)
  ) {
    return { valid: false, type: "invalid-calendar-connection", message: `Invalid calendar connection` };
  }

  if (!isCalendarValid) {
    return { valid: false, type: "no-calendar-id", message: `Invalid Calendar ID` };
  }

  if (!calendarManagement.updatedIsSameAsOpeningHours && calendarManagement.updatedSchedulingWindows.length === 0) {
    return {
      valid: false,
      type: "no-scheduling-windows",
      message: `Please add at least one scheduling window.`,
    };
  }

  if (calendarManagement.updatedIsSameAsOpeningHours && openingHours.length === 0) {
    return {
      valid: false,
      type: "no-scheduling-windows",
      message: `Please add at least one scheduling window.`,
    };
  }

  if (calendarManagement.updatedAppointmentTypes.length === 0) {
    return {
      valid: false,
      type: "no-appointment-types",
      message: `Please add at least one appointment type.`,
    };
  }

  if (
    calendarManagement.config.minAppointmentTypesListSize > 0 &&
    calendarManagement.updatedAppointmentTypes.length < calendarManagement.config.minAppointmentTypesListSize
  ) {
    const errorMessage = `You need to add at least ${
      calendarManagement.config.minAppointmentTypesListSize === 1
        ? "1 appointment type"
        : calendarManagement.config.minAppointmentTypesListSize + " appointment types"
    }`;

    return {
      valid: false,
      type: "min-appointment-types",
      message: errorMessage,
    };
  }

  if (!calendarManagement.updatedIsSameAsOpeningHours) {
    for (var i = 0; i < calendarManagement.updatedSchedulingWindows.length; i++) {
      const schedulingWindow = calendarManagement.updatedSchedulingWindows[i];
      const isValidSchedulingWindow = validateSchedulingWindow(i, schedulingWindow, calendarManagement.config);
      if (!isValidSchedulingWindow.valid) {
        return isValidSchedulingWindow;
      }
    }
  }

  for (var i = 0; i < calendarManagement.updatedAppointmentTypes.length; i++) {
    const appointmentType = calendarManagement.updatedAppointmentTypes[i];
    const isValidAppointmentType = validateAppointmentType(i, appointmentType, calendarManagement.config);
    if (!isValidAppointmentType.valid) {
      // update errors item
      const appointmentTypeErrors = [...calendarManagement.appointmentTypeErrors];
      appointmentTypeErrors[i] = getAppointmentTypeErrors(appointmentType);
      store.dispatch(updateAppointmentTypeErrors(appointmentTypeErrors));
      return isValidAppointmentType;
    }
  }

  return { valid: true };
};

export const validateSchedulingWindow = (index, schedulingWindow, config) => {
  const response = {
    valid: false,
    type: "invalid-scheduling-window",
    message: "",
    elementId: `scheduling-window-${index}`,
    accordionIndex: index,
  };
  if (schedulingWindow.day === "") {
    return { ...response, message: `Scheduling Window(${index}) - Invalid Day` };
  }

  if (schedulingWindow.open === "") {
    return { ...response, message: `Scheduling Window(${index}) - Invalid Start Time` };
  }

  if (schedulingWindow.close === "") {
    return { ...response, message: `Scheduling Window(${index}) - Invalid End Time` };
  }

  return { valid: true };
};

export const validateAppointmentType = (index, appointmentType) => {
  const config = store.getState().calendarManagement.config;

  var response = {
    valid: false,
    type: "invalid-appointment-type",
    message: "",
    elementId: `appointment-type-${index}`,
    accordionIndex: index,
  };
  if (appointmentType.type.trim() === "" || appointmentType.type.trim().length < config.minAppointmentTypeCharacters) {
    return { ...response, message: `Appointment(${index}) - Invalid Type` };
  }

  if (!appointmentType.duration) {
    return { ...response, message: `Appointment(${index}) - Invalid Duration` };
  }

  if (appointmentType.description.trim().length < config.minAppointmentDescriptionCharacters) {
    return { ...response, message: `Appointment(${index}) - Invalid Description` };
  }

  return { valid: true };
};

export const getCalendarManagementSaveObject = async () => {
  const calendarManagement = store.getState().calendarManagement;
  const { organizationId, accountId, workspaceId, token } = store.getState().info;
  const { productName } = store.getState().info.organization;
  const credentialsKey = getOrganizationFeatureFlag("SR-SNIPPET", "credentialsKey");

  var calendarInfo = calendarManagement.updatedCalendarInfo;
  if (calendarInfo.id === TEMP_CALENDAR_ID) {
    var calendarName = `[${productName}] #${workspaceId} (${accountId},${organizationId})`;

    const secondaryCalendar = await addSecondaryCalendarInfo({
      aliasName: calendarInfo.aliasName,
      calendarName: calendarName,
      calendarProvider: calendarInfo.provider,
      credentialsKey: credentialsKey,
      token: token,
      organizationId,
      accountId,
    });

    calendarInfo.id = secondaryCalendar.calendarId;
    calendarInfo.credentialsKey = credentialsKey;
  }

  const trimmedAppointmentTypes = calendarManagement.updatedAppointmentTypes.map((appointmentType) => {
    return {
      ...appointmentType,
      type: appointmentType.type.trim(),
      description: appointmentType.description.trim(),
      postSchedulingTextMessage: appointmentType.postSchedulingTextMessage?.trim(),
    };
  });

  let schedulingWindows = calendarManagement.updatedSchedulingWindows;
  if (calendarManagement.updatedIsSameAsOpeningHours) {
    const knowledgeBase = store.getState().knowledgeBase;
    const openingHours =
      knowledgeBase.updatedKnowledgeBaseItems.find((item) => item.type === OPENING_HOURS_KEY)?.value || [];
    schedulingWindows = openingHours;
  }

  return {
    calendarInfo: calendarInfo,
    schedulingWindows,
    appointmentTypes: trimmedAppointmentTypes,
    isSameAsOpeningHours: calendarManagement.updatedIsSameAsOpeningHours,
  };
};

export const isEmptyAppointmentTypeSection = (appointmentType) => {
  return appointmentType.type === "" && appointmentType.description === "";
};
